import ReactGA from 'react-ga4';
import { toast } from 'sonner';
import { FaStar } from 'react-icons/fa6';
import { useTranslation } from 'react-i18next';
/////////////////////////////////////////////////////////
import { telegramWebApp } from '@/main';
import { BuyResponse, Product as ProductInterface } from '@/core/interfaces';
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
	Button,
} from '@/app/shared/components';
import { ShopService } from '@/core/services';
import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useCustomDispatch } from '@/core/redux/hooks';
import {
	setCounter,
	setDiamonds,
	setStickerWonDialog,
} from '@/core/redux/slices';

interface ProductProps {
	product: ProductInterface;
	showConfetti: () => void;
}

export const Product: React.FC<ProductProps> = ({
	product,
	showConfetti,
}: ProductProps) => {
	/*********************
	// #region Hooks
	**********************/
	const { t } = useTranslation();
	const dispatch = useCustomDispatch();

	/*********************
	// #region State
	**********************/
	const [successPurchase, setSuccessPurchase] = useState<boolean>(false);
	const [purchaseStatus, setPurchaseStatus] = useState<
		'paid' | 'cancelled' | 'failed' | 'pending'
	>('pending');

	/*********************
	// #region Observables
	**********************/
	useEffect(() => {
		if (!successPurchase) return;

		toast.promise(ShopService.checkPurchase(), {
			loading: t('general.loading'),
			success: (data: BuyResponse) => {
				// Emit event to Google Analytics
				ReactGA.event({
					category: 'shop',
					action: `Buy ${product.title}`,
				});

				console.log(purchaseStatus);

				showConfetti();

				// Update counter
				dispatch(setCounter(data.currentCoins));
				// Update diamonds
				dispatch(setDiamonds(data.currentDiamonds));

				if (data.isAwardWinning) {
					dispatch(setStickerWonDialog(true));
				}

				setSuccessPurchase(false);

				return `${t('shop.buy.successBuy')}`;
			},
			error: `${t('shop.buy.errorBuy')}`,
		});
	}, [successPurchase]);

	/*********************
	// #region Functions
	**********************/
	const handleBuy = () => {
		telegramWebApp.HapticFeedback.impactOccurred('heavy');

		toast.promise(ShopService.buyProduct(product.id), {
			loading: t('shop.buy.processingBuy'),
			success: (data: any) => {
				// Emit event to Google Analytics
				telegramWebApp.openInvoice(
					data.invoiceLink,
					(status: 'paid' | 'cancelled' | 'failed' | 'pending') => {
						if (status === 'paid') {
							setSuccessPurchase(true);
						}
						setPurchaseStatus(status);
					}
				);

				return `${t('shop.buy.readyBuy')}`;
			},
			error: t('shop.buy.errorBuy'),
		});
	};

	/*********************
	// #region Template
	**********************/
	return (
		<div className="flex flex-col rounded-2xl items-center bg-vottun-900/50 px-3 py-6">
			<div className="mb-4 text-center">
				<img src={product.image} alt={product.title} />
			</div>

			<div className="flex flex-col gap-2">
				<p className="text-sm font-bold">{product.title}</p>
				<p className="text-[10px]">{product.description}</p>
			</div>

			<div className="mt-6 w-full">
				<AlertDialog>
					<AlertDialogTrigger asChild>
						<Button
							variant="secondary"
							className={
								'text-sm py-2 flex items-center justify-center gap-2 w-full'
							}
						>
							<div color="text-yellow-400">
								<FaStar color="yellow" />
							</div>
							<p className="leading-none">{product.stars}</p>
						</Button>
					</AlertDialogTrigger>
					<AlertDialogContent className="w-[90%]">
						<AlertDialogHeader>
							<AlertDialogTitle>
								{t('shop.buyAlert.title')}
							</AlertDialogTitle>
							<AlertDialogDescription>
								{t('shop.buyAlert.text')}{' '}
								<Link
									to="https://shurikendojo.xyz/terms-and-conditions"
									target="_blank"
									className="text-customer-500"
								>
									{t('shop.buyAlert.terms')}
								</Link>{' '}
							</AlertDialogDescription>
						</AlertDialogHeader>
						<AlertDialogFooter>
							<AlertDialogCancel>
								{t('shop.buyAlert.cancel')}
							</AlertDialogCancel>
							<AlertDialogAction onClick={handleBuy}>
								{t('shop.buyAlert.confirm')}
							</AlertDialogAction>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialog>
			</div>
		</div>
	);
};
